import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/query-keys.js';
import { deliverableFoldersApi } from '../lib/api/deliverable-folders.js';

/**
 * 成果物種別ごとの、この案件のフォルダの有無を取る。Drive を種別の数だけ問い合わせるため、
 * 欄を開いたとき（`enabled`）だけ取得する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件のデザイン成果物フォルダの種別ごとの有無を、欄を開いたときだけ取得する
 * @graph-connects /projects/{issueKey}/deliverable-folders [calls] 成果物フォルダの一覧API
 * @graph-connects deliverableFoldersApi.view [calls] 一覧APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useDeliverableFolders(pjKey: string, options: { enabled: boolean }) {
  return useQuery({
    queryKey: queryKeys.projects.deliverableFolders(pjKey),
    queryFn: () => deliverableFoldersApi.view(pjKey),
    enabled: options.enabled,
    // 400（デザイン子チケットが無い等）は再試行しても変わらない
    retry: false,
  });
}

/**
 * 選んだ種別の成果物フォルダを作り、成功したら一覧を取り直す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 選んだ種別のデザイン成果物フォルダを作り、一覧を取り直す
 * @graph-connects /projects/{issueKey}/deliverable-folders [calls] 成果物フォルダの作成API
 * @graph-connects deliverableFoldersApi.create [calls] 作成APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useCreateDeliverableFolders(pjKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (types: string[]) => deliverableFoldersApi.create(pjKey, types),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projects.deliverableFolders(pjKey),
      });
    },
  });
}
